import { useMemo } from "react";
import type { Poll, PollOption } from "@tinyclub/shared/models";
import { cn } from "@/components/ui/cn";

interface PollDisplayProps {
  poll: Poll;
  onVote: (optionId: string) => void;
  isVoting?: boolean;
  currentUserId?: string | null;
}

/**
 * Poll viewing/voting component for PostCard.
 * Shows poll options as clickable bars with progress indicators.
 * After voting or when poll is closed, shows vote percentages.
 * Matches mobile PollDisplay styling.
 */
export function PollDisplay({
  poll,
  onVote,
  isVoting = false,
  currentUserId: _currentUserId,
}: PollDisplayProps) {
  // Check if user has voted
  const hasVoted = !!poll.user_vote_option_id;

  // Check if poll is closed (either manually or by time)
  const isExpired = new Date(poll.closes_at) <= new Date();
  const isClosed = poll.is_closed || isExpired;

  // Show results if user has voted or poll is closed
  const showResults = hasVoted || isClosed;

  // Calculate time remaining
  const timeRemaining = useMemo(() => {
    if (isClosed) return "Closed";

    const now = new Date();
    const closesAt = new Date(poll.closes_at);
    const diffMs = closesAt.getTime() - now.getTime();

    if (diffMs <= 0) return "Closed";

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d left`;
    } else if (diffHours > 0) {
      return `${diffHours}h left`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}m left`;
    }
  }, [poll.closes_at, isClosed]);

  // Sort options by sort_order
  const sortedOptions = useMemo(() => {
    return [...poll.options].sort((a, b) => a.sort_order - b.sort_order);
  }, [poll.options]);

  const handleOptionClick = (e: React.MouseEvent, option: PollOption) => {
    e.stopPropagation();
    if (isClosed || isVoting) return;
    onVote(option.id);
  };

  const renderOption = (option: PollOption) => {
    const isSelected = poll.user_vote_option_id === option.id;
    const percentage =
      poll.total_votes > 0 ? (option.vote_count / poll.total_votes) * 100 : 0;
    const roundedPercentage = Math.round(percentage);
    const isFull = roundedPercentage === 100;

    return (
      <button
        key={option.id}
        type="button"
        onClick={(e) => handleOptionClick(e, option)}
        disabled={isClosed || isVoting}
        className={cn(
          "relative w-full rounded-[12px] overflow-hidden text-left cursor-pointer",
          "border border-border-tertiary transition-colors",
          !showResults && !isVoting && "hover:bg-surface-1"
        )}
      >
        {/* Fill bar (only show when results visible) */}
        {showResults && (
          <div
            className={cn(
              "absolute top-0 left-0 bottom-0 transition-all duration-300",
              isSelected ? "bg-text-primary" : "bg-surface-2"
            )}
            style={{
              width: `${percentage}%`,
              borderRadius: isFull
                ? "11px"
                : "11px 6px 6px 11px",
            }}
          />
        )}

        {/* Base text layer (dark) */}
        <div className="relative flex items-center justify-between px-3 py-2 min-h-[44px]">
          <div className="flex-1 mr-3">
            <span className="text-body-2-medium text-text-primary">
              {option.option_text}
            </span>
          </div>

          {showResults && (
            <span className="text-body-3-medium text-text-secondary">
              {roundedPercentage}%
            </span>
          )}
        </div>

        {/* White text overlay clipped to fill bar width (selected options only) */}
        {showResults && isSelected && roundedPercentage > 0 && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              clipPath: `inset(0 ${100 - percentage}% 0 0)`,
            }}
          >
            <div className="flex items-center justify-between px-3 py-2 min-h-[44px]">
              <div className="flex-1 mr-3">
                <span className="text-body-2-medium text-text-reversed">
                  {option.option_text}
                </span>
              </div>
              <span className="text-body-3-medium text-text-reversed">
                {roundedPercentage}%
              </span>
            </div>
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="mt-1">
      {/* Poll options */}
      <div className="space-y-2">{sortedOptions.map(renderOption)}</div>

      {/* Footer with total votes and time remaining */}
      <div className="flex items-center gap-1.5 mt-3 ml-2 text-body-3 text-text-tertiary">
        {showResults && (
          <>
            <span>
              {poll.total_votes} {poll.total_votes === 1 ? "vote" : "votes"}
            </span>
            <span>&middot;</span>
          </>
        )}
        <span>{timeRemaining}</span>
        {isVoting && (
          <div className="ml-2 w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
        )}
      </div>
    </div>
  );
}
